import { Check, Copy, Facebook, Linkedin, Share2 } from 'lucide-react'
import * as React from 'react'
import { FaXTwitter } from 'react-icons/fa6'

import { cn } from '@/lib/utils'
import { toast } from '@/hooks/use-toast'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

import { generateSocialShareUrl } from '@/utils/seo-utils'

/**
 * Enhanced Social Share Buttons Component
 *
 * This component provides comprehensive social sharing functionality with:
 * - Title prominently displayed in share text and URL parameters
 * - Description and hashtags included in sharing
 * - Embed URL support for content that can be embedded
 * - Copy to clipboard functionality with formatted text
 * - UTM tracking parameters for analytics
 *
 * @example
 * ```tsx
 * <SocialShareButtons
 *   url="https://fullstackdatascience.com/blogs/my-article"
 *   title="Amazing Data Science Article"
 *   description="Learn about the latest trends in data science"
 *   hashtags={['datascience', 'machinelearning', 'ai']}
 *   embedUrl="https://fullstackdatascience.com/embed/blogs/my-article"
 * />
 * ```
 */
interface SocialShareButtonsProps {
  /** The URL to be shared */
  url: string
  /** The title of the content being shared - prominently displayed */
  title: string
  /** Description of the content being shared */
  description: string
  /** Additional CSS classes */
  className?: string
  /** Button variant */
  variant?: 'default' | 'outline' | 'ghost'
  /** Button size */
  size?: 'default' | 'sm' | 'lg' | 'icon'
  /** Text content of the button */
  textContent?: React.ReactNode
  /** The id of the content being shared */
  id?: string
  /** The type of the content being shared */
  type?: 'meme' | 'post' | 'topic'
  /** Whether the popover is modal */
  modal?: boolean
  /** The slug of the course */
  courseSlug?: string
  /** Base path for course topic deep links (default: my-courses) */
  coursePathPrefix?: 'courses' | 'my-courses'
  /** Discussion list page for topic deep links */
  page?: number
  /** Hashtags for share text and URL metadata (same pattern as GroupSharingButton / blogs) */
  hashtags?: string[]
  /** Optional embed URL for platforms that support it */
  embedUrl?: string
  /** When true, renders only a copy-link button (no share popover). */
  copyLinkOnly?: boolean
}

export function SocialShareButtons({
  url,
  title,
  description,
  id,
  type,
  className = '',
  variant = 'ghost',
  size = 'default',
  textContent = (
    <>
      <span className="hidden sm:inline">Chia sẻ</span>
    </>
  ),
  modal = true,
  courseSlug,
  coursePathPrefix = 'my-courses',
  page,
  hashtags,
  embedUrl,
  copyLinkOnly = false,
}: SocialShareButtonsProps) {
  const [copied, setCopied] = React.useState(false)

  const getShareUrl = () => {
    if (type === 'meme') {
      return `${window.location.origin}/meme/shared/${id}`
    } else if (type === 'post') {
      return `${window.location.origin}/forum/shared/${id}`
    } else if (type === 'topic' && courseSlug) {
      const pageQuery = page && page > 1 ? `&page=${page}` : ''
      return `${window.location.origin}/${coursePathPrefix}/${courseSlug}?topicId=${id}${pageQuery}`
    }
    return url
  }

  const shareMetadata = React.useMemo(
    () => ({
      title,
      description,
      hashtags,
      embedUrl,
    }),
    [title, description, hashtags, embedUrl]
  )

  const handleCopyLink = async () => {
    try {
      const textToCopy = getShareUrl()
      await navigator.clipboard.writeText(textToCopy)
      setCopied(true)
      toast({
        title: 'Link copied!',
        description: 'The link has been copied to your clipboard.',
      })
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      toast({
        title: 'Failed to copy',
        description: 'Please try copying the link manually.',
        variant: 'destructive',
      })
    }
  }

  const handleFacebookShare = () => {
    const shareURL = generateSocialShareUrl(getShareUrl(), 'facebook', shareMetadata)
    window.open(shareURL, '_blank', 'width=600,height=400')
  }

  const handleLinkedInShare = () => {
    const shareUrl = generateSocialShareUrl(getShareUrl(), 'linkedin', shareMetadata)
    window.open(shareUrl, '_blank', 'width=600,height=400')
  }

  const handleTwitterShare = () => {
    const shareUrl = generateSocialShareUrl(getShareUrl(), 'twitter', shareMetadata)
    window.open(shareUrl, '_blank', 'width=600,height=400')
  }

  if (copyLinkOnly) {
    return (
      <Button
        variant={variant}
        size={size}
        onClick={handleCopyLink}
        className={cn('flex items-center gap-2 text-gray-600 hover:bg-gray-100', className)}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        <span className="hidden sm:inline">{copied ? 'Copied!' : 'Copy link'}</span>
      </Button>
    )
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Popover modal={modal}>
        <PopoverTrigger asChild>
          <Button
            variant={variant}
            size={size}
            className="flex items-center gap-2 w-full justify-center text-gray-600 hover:bg-gray-100"
          >
            <Share2 className="h-4 w-4" />
            {textContent}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2 !z-[100000000]" align="end">
          <div className="flex flex-col gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleFacebookShare}
              className="justify-start gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              <Facebook className="h-4 w-4" />
              Share on Facebook
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleLinkedInShare}
              className="justify-start gap-2 text-sky-600 hover:text-sky-700 hover:bg-sky-50"
            >
              <Linkedin className="h-4 w-4" />
              Share on LinkedIn
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleTwitterShare}
              className="justify-start gap-2 text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
            >
              <FaXTwitter className="h-4 w-4" />
              Share on X
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopyLink}
              className="justify-start gap-2 text-gray-600 hover:text-gray-700 hover:bg-gray-100"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

/**
 * Quick Share Buttons Component
 *
 * A simplified version of social sharing buttons without the popover interface.
 * Provides direct access to Facebook, LinkedIn, and X sharing with the same
 * enhanced functionality as SocialShareButtons.
 *
 * @example
 * ```tsx
 * <QuickShareButtons
 *   url="https://fullstackdatascience.com/blogs/my-article"
 *   title="Amazing Data Science Article"
 *   description="Learn about the latest trends in data science"
 *   hashtags={['datascience', 'machinelearning', 'ai']}
 *   embedUrl="https://fullstackdatascience.com/embed/blogs/my-article"
 * />
 * ```
 */
interface QuickShareButtonsProps {
  /** The URL to be shared */
  url: string
  /** The title of the content being shared - prominently displayed */
  title: string
  /** Description of the content being shared */
  description: string
  /** Additional CSS classes */
  className?: string
  hashtags?: string[]
  embedUrl?: string
}

export function QuickShareButtons({
  url,
  title,
  description,
  className = '',
  hashtags,
  embedUrl,
}: QuickShareButtonsProps) {
  const shareMetadata = React.useMemo(
    () => ({
      title,
      description,
      hashtags,
      embedUrl,
    }),
    [title, description, hashtags, embedUrl]
  )

  const handleFacebookShare = () => {
    const shareUrl = generateSocialShareUrl(url, 'facebook', shareMetadata)
    window.open(shareUrl, '_blank', 'width=600,height=400')
  }

  const handleLinkedInShare = () => {
    const shareUrl = generateSocialShareUrl(url, 'linkedin', shareMetadata)
    window.open(shareUrl, '_blank', 'width=600,height=400')
  }

  const handleTwitterShare = () => {
    const shareUrl = generateSocialShareUrl(url, 'twitter', shareMetadata)
    window.open(shareUrl, '_blank', 'width=600,height=400')
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleFacebookShare}
        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
        title={`Share "${title}" on Facebook`}
      >
        <Facebook className="h-4 w-4" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={handleLinkedInShare}
        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
        title={`Share "${title}" on LinkedIn`}
      >
        <Linkedin className="h-4 w-4" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={handleTwitterShare}
        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
        title={`Share "${title}" on X`}
      >
        <FaXTwitter className="h-4 w-4" />
      </Button>
    </div>
  )
}
